(() => {
  if (customElements.get('mt-ugc-media-carousel')) return;

  class MtUgcMediaCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.status = this.querySelector('[data-ugc-status]');
      this.track = this.querySelector('[data-ugc-track]');
      this.trackControls = this.querySelector('[data-ugc-track-controls]');
      this.previousTrackControl = this.querySelector('[data-ugc-track-scroll="previous"]');
      this.nextTrackControl = this.querySelector('[data-ugc-track-scroll="next"]');
      this.dialog = this.querySelector('[data-ugc-dialog]');
      this.dialogMedia = this.querySelector('[data-ugc-dialog-media]');
      this.dialogStatus = this.querySelector('[data-ugc-dialog-status]');
      this.defaultDialogLabel = this.dialog?.getAttribute('aria-label') || '';
      this.videoPositionTemplate = this.dataset.videoPositionTemplate || '';
      this.lastTrigger = null;
      this.currentIndex = -1;
      this.mediaGeneration = 0;
      this.readyTimer = 0;
      this.videoFrameCallbackId = null;
      this.activeVideo = null;
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.addEventListener('click', this.onClick.bind(this), { signal });
      this.addEventListener('submit', this.onSubmit.bind(this), { signal });
      this.track?.addEventListener('keydown', this.onTrackKeydown.bind(this), { signal });
      this.track?.addEventListener('scroll', () => this.scheduleTrackControlUpdate(), { passive: true, signal });
      this.dialog?.addEventListener('click', this.onDialogClick.bind(this), { capture: true, signal });
      this.dialog?.addEventListener('keydown', this.onDialogKeydown.bind(this), { capture: true, signal });
      this.dialog?.addEventListener('close', this.onDialogClose.bind(this), { signal });
      this.dialog?.addEventListener('cancel', this.onDialogCancel.bind(this), { signal });
      if ('ResizeObserver' in window && this.track) {
        this.trackResizeObserver = new ResizeObserver(() => this.scheduleTrackControlUpdate());
        this.trackResizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', () => this.scheduleTrackControlUpdate(), { passive: true, signal });
      }
      this.scheduleTrackControlUpdate();
    }

    disconnectedCallback() {
      if (this.trackControlFrame) cancelAnimationFrame(this.trackControlFrame);
      this.trackResizeObserver?.disconnect();
      this.abortController?.abort();
      this.clearDialogMedia();
      if (typeof this.dialog?.close === 'function' && this.dialog.open) this.dialog.close();
      this.dialog?.removeAttribute('open');
      this.lastTrigger = null;
      this.currentIndex = -1;
      this.updateDialogStatus('');
      this.initialized = false;
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;
      const trackControl = event.target.closest('[data-ugc-track-scroll]');
      if (trackControl instanceof HTMLButtonElement) {
        event.preventDefault();
        this.scrollTrack(trackControl.dataset.ugcTrackScroll === 'previous' ? -1 : 1);
        return;
      }
      if (event.target.closest('[data-ugc-close]')) {
        this.closeDialog();
        return;
      }
      const step = event.target.closest('[data-ugc-step]');
      if (step instanceof HTMLButtonElement) {
        event.preventDefault();
        this.stepDialog(Number(step.dataset.ugcStep));
        return;
      }
      const trigger = event.target.closest('[data-ugc-open]');
      if (trigger instanceof HTMLElement) {
        event.preventDefault();
        this.openDialog(trigger);
      }
    }

    onTrackKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const cards = Array.from(this.track?.querySelectorAll('[data-ugc-card]') || []);
      if (!cards.length || !this.track) return;
      event.preventDefault();
      const step = cards.length > 1
        ? Math.abs(cards[1].offsetLeft - cards[0].offsetLeft)
        : cards[0].offsetWidth;
      this.track.scrollBy({
        left: (event.key === 'ArrowRight' ? 1 : -1) * step,
        behavior: this.prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    scheduleTrackControlUpdate() {
      if (this.trackControlFrame) return;
      this.trackControlFrame = requestAnimationFrame(() => {
        this.trackControlFrame = 0;
        this.updateTrackControls();
      });
    }

    updateTrackControls() {
      if (!this.track || !this.trackControls) return;
      const maximumScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const currentScroll = Math.abs(this.track.scrollLeft);
      const hasOverflow = maximumScroll > 1;
      this.trackControls.hidden = !hasOverflow;
      if (this.previousTrackControl) this.previousTrackControl.disabled = !hasOverflow || currentScroll <= 1;
      if (this.nextTrackControl) this.nextTrackControl.disabled = !hasOverflow || currentScroll >= maximumScroll - 1;
    }

    scrollTrack(direction) {
      if (!this.track || !Number.isFinite(direction)) return;
      const card = this.track.querySelector('[data-ugc-card]');
      if (!(card instanceof HTMLElement)) return;
      const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = card.getBoundingClientRect().width + gap;
      const visibleCards = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));
      this.track.scrollBy({
        left: direction * step * visibleCards,
        behavior: this.prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    onDialogClick(event) {
      if (!(event.target instanceof Element) || !event.target.closest('[data-ugc-close]')) return;
      event.preventDefault();
      event.stopPropagation();
      this.closeDialog();
    }

    onDialogKeydown(event) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      this.closeDialog();
    }

    async onSubmit(event) {
      if (!(event.target instanceof HTMLFormElement) || !event.target.matches('[data-ugc-add-form]')) return;
      event.preventDefault();
      const submit = event.target.querySelector('[data-ugc-add]');
      const id = event.target.querySelector('[name="id"]')?.value;
      if (!(submit instanceof HTMLButtonElement) || !id) return;
      if (this.status) this.status.textContent = '';
      submit.disabled = true;
      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id, quantity: 1 })
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || payload.message);
        if (this.status) this.status.textContent = this.dataset.addedMessage || '';
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'product', origin: 'ugc-carousel' } }));
      } catch (error) {
        if (this.status) this.status.textContent = error.message || this.dataset.errorMessage;
      } finally {
        submit.disabled = false;
      }
    }

    openDialog(trigger) {
      const source = trigger.dataset.videoSrc || trigger.dataset.imageSrc;
      if (!source || !this.dialog || !this.dialogMedia) return;
      this.lastTrigger = trigger;
      if (trigger.dataset.imageSrc) {
        this.clearDialogMedia();
        const image = document.createElement('img');
        image.src = source;
        image.alt = trigger.dataset.imageAlt || '';
        this.dialogMedia.append(image);
        this.dialog.setAttribute('aria-label', image.alt || this.defaultDialogLabel);
        this.updateDialogStatus('');
        this.showDialog();
        return;
      }

      const triggers = this.getVideoTriggers();
      const index = triggers.indexOf(trigger);
      if (index < 0) return;

      this.currentIndex = index;
      this.dialog.setAttribute('aria-label', this.defaultDialogLabel);
      const video = this.renderVideo(triggers);
      this.showDialog();
      video?.play().catch(() => {});
    }

    getVideoTriggers() {
      return Array.from(this.querySelectorAll('[data-ugc-open][data-video-src]'))
        .filter((trigger) => trigger instanceof HTMLElement && trigger.dataset.videoSrc);
    }

    stepDialog(step) {
      const triggers = this.getVideoTriggers();
      if (triggers.length < 2 || !Number.isFinite(step)) return;
      this.currentIndex = (this.currentIndex + step + triggers.length) % triggers.length;
      const video = this.renderVideo(triggers);
      video?.play().catch(() => {});
    }

    renderVideo(triggers) {
      const trigger = triggers[this.currentIndex];
      const source = trigger?.dataset.videoSrc;
      if (!source || !this.dialogMedia) return;

      this.clearDialogMedia();
      const mediaGeneration = this.mediaGeneration;
      const video = document.createElement('video');
      video.controls = false;
      video.playsInline = true;
      video.preload = 'none';
      this.dialogMedia.setAttribute('aria-busy', 'true');
      this.dialogMedia.dataset.mediaState = 'loading';
      this.activeVideo = video;
      const mediaSource = document.createElement('source');
      mediaSource.src = source;
      if (trigger.dataset.videoType) mediaSource.type = trigger.dataset.videoType;
      video.append(mediaSource);
      let readyRequested = false;
      const commitReady = () => {
        if (mediaGeneration !== this.mediaGeneration || video !== this.activeVideo) return;
        this.cancelReadyWait(video);
        video.controls = true;
        this.dialogMedia.removeAttribute('aria-busy');
        this.dialogMedia.dataset.mediaState = 'ready';
      };
      const requestReady = () => {
        if (readyRequested) return;
        readyRequested = true;
        if (typeof video.requestVideoFrameCallback === 'function') {
          this.videoFrameCallbackId = video.requestVideoFrameCallback(commitReady);
          this.readyTimer = window.setTimeout(commitReady, 250);
          return;
        }
        commitReady();
      };
      video.addEventListener('loadeddata', requestReady, { once: true });
      video.addEventListener('canplay', requestReady, { once: true });
      video.addEventListener('error', () => {
        if (mediaGeneration !== this.mediaGeneration || video !== this.activeVideo) return;
        this.cancelReadyWait(video);
        const message = this.dataset.videoErrorMessage || this.dataset.errorMessage || '';
        this.dialogMedia.removeAttribute('aria-busy');
        this.dialogMedia.dataset.mediaState = 'error';
        if (this.status) this.status.textContent = message;
        this.updateDialogStatus(message);
      }, { once: true });
      this.dialogMedia.append(video);
      video.load();
      this.updateDialogStatus(this.formatVideoPosition(this.currentIndex + 1, triggers.length));
      return video;
    }

    formatVideoPosition(current, total) {
      return this.videoPositionTemplate
        .replace('__current__', String(current))
        .replace('__total__', String(total));
    }

    updateDialogStatus(message) {
      if (this.dialogStatus) this.dialogStatus.textContent = message;
    }

    showDialog() {
      if (typeof this.dialog?.showModal === 'function') this.dialog.showModal();
      else this.dialog?.setAttribute('open', '');
    }

    closeDialog() {
      if (!this.dialog) return;
      this.clearDialogMedia();
      if (typeof this.dialog.close === 'function' && this.dialog.open) this.dialog.close();
      if (this.dialog.open || this.dialog.hasAttribute('open')) {
        this.dialog.removeAttribute('open');
        this.onDialogClose();
        return;
      }
      if (this.lastTrigger) {
        this.lastTrigger.focus();
        this.lastTrigger = null;
      }
    }

    onDialogCancel(event) {
      event.preventDefault();
      this.closeDialog();
    }

    onDialogClose() {
      this.clearDialogMedia();
      this.lastTrigger?.focus();
      this.lastTrigger = null;
      this.currentIndex = -1;
      this.updateDialogStatus('');
    }

    clearDialogMedia() {
      this.mediaGeneration += 1;
      const video = this.activeVideo || this.dialogMedia?.querySelector('video');
      this.cancelReadyWait(video);
      this.activeVideo = null;
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach((source) => source.remove());
        video.load();
      }
      this.dialogMedia?.replaceChildren();
      this.dialogMedia?.removeAttribute('aria-busy');
      if (this.dialogMedia) delete this.dialogMedia.dataset.mediaState;
    }

    cancelReadyWait(video) {
      if (this.readyTimer) {
        window.clearTimeout(this.readyTimer);
        this.readyTimer = 0;
      }
      if (
        this.videoFrameCallbackId !== null
        && video
        && typeof video.cancelVideoFrameCallback === 'function'
      ) {
        video.cancelVideoFrameCallback(this.videoFrameCallbackId);
      }
      this.videoFrameCallbackId = null;
    }
  }

  customElements.define('mt-ugc-media-carousel', MtUgcMediaCarousel);
})();
